/**
 * CR-8252 (decision 6, Aaron 12 Oct 2026): an approved supplier stays Approved
 * while a questionnaire approval nears or passes its expiry; a flag beside the
 * status says so. "Renewal due" within 30 days of expiry, "Renewal overdue"
 * once past it. One definition for every screen that shows the flag.
 */
import type { FilledDataset } from './api';

export type RenewalFlag = 'due' | 'overdue';

export const RENEWAL_FLAG_BADGE: Record<RenewalFlag, { label: string; className: string }> = {
  due: { label: 'Renewal due', className: 'bg-amber-100 text-amber-800' },
  overdue: { label: 'Renewal overdue', className: 'bg-red-100 text-red-800' },
};

/**
 * The flag for one questionnaire, from the server's renewal status.
 *
 * @param row - a questionnaire (the server sets renewal_status on approved ones)
 * @returns the flag, or null when nothing is due
 */
export function renewalFlagOf(row: Pick<FilledDataset, 'renewal_status'>): RenewalFlag | null {
  if (row.renewal_status === 'overdue') return 'overdue';
  if (row.renewal_status === 'due_soon') return 'due';
  return null;
}

/**
 * The worst flag across a supplier's questionnaires: overdue beats due.
 *
 * @param rows - the supplier's questionnaires
 * @returns the flag to show beside the supplier's status, or null
 */
export function supplierRenewalFlag(rows: Pick<FilledDataset, 'renewal_status'>[]): RenewalFlag | null {
  let flag: RenewalFlag | null = null;
  for (const row of rows) {
    const f = renewalFlagOf(row);
    if (f === 'overdue') return 'overdue';
    if (f === 'due') flag = 'due';
  }
  return flag;
}

/**
 * The flag from a supplier list row's questionnaire summary (the same summary
 * the Suppliers card badge reads).
 *
 * @param overall - ``questionnaire_status.overall`` from the supplier list
 * @returns the flag, or null when nothing is due
 */
export function renewalFlagFromSummary(overall: string | undefined): RenewalFlag | null {
  if (overall === 'overdue') return 'overdue';
  if (overall === 'expiring') return 'due';
  return null;
}
